import { ErrorHandler, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { EnvironmentVariables } from '../../environment-variables';
import { LogLevel } from '../models';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private appInsights$ = new BehaviorSubject<ApplicationInsights | undefined>(
    undefined
  );
  private level: LogLevel = LogLevel.Warn;
  public verboseLogging = false;

  constructor(
    private environment: EnvironmentVariables,
    private router: Router,
    private http: HttpClient
  ) {
    this.level = environment.logLevel;
    //console.log('[LoggerService] level', this.level);
  }

  private logToAppInsights(entry: LogEntry) {
    this.appInsights$
      .pipe(
        filter((ai) => !!ai),
        take(1)
      )
      .subscribe((appInsights) =>
        appInsights!.trackTrace(
          {
            message: entry.buildAppInsightString(),
            severityLevel: entry.level - 1,
          },
          {
            ...{
              ApplicationName: this.environment.authClientId,
              SourceContext: entry.sourceContext,
            },
            ...entry.extraInfo,
          }
        )
      );
  }

  private shouldLog(level: LogLevel): boolean {
    let ret: boolean = false;
    if (
      (level >= this.level && level !== LogLevel.Off) ||
      this.level === LogLevel.All
    ) {
      ret = true;
    }
    return ret;
  }

  debug = (
    sourceContext: string,
    message: string,
    ...optionalParams: any[]
  ) => {
    const entry = new LogEntry(
      sourceContext,
      message,
      LogLevel.Debug,
      optionalParams
    );
    if (!this.shouldLog(entry.level)) return () => {};
    this.logToAppInsights(entry);
    return console.debug.bind(
      console,
      ...[
        entry.buildConsoleString(),
        'color:gray',
        'color:violet',
        ...entry.extraInfo.map((ei) => JSON.stringify(ei)),
      ]
    );
  };

  error = (
    sourceContext: string,
    message: string,
    ...optionalParams: any[]
  ) => {
    const entry = new LogEntry(
      sourceContext,
      message,
      LogLevel.Error,
      optionalParams
    );
    if (!this.shouldLog(entry.level)) return () => {};
    this.logToAppInsights(entry);
    return console.error.bind(
      console,
      ...[
        entry.buildConsoleString(),
        'color:red',
        'color:violet',
        ...entry.extraInfo.map((ei) => JSON.stringify(ei)),
      ]
    );
  };

  info = (sourceContext: string, message: string, ...optionalParams: any[]) => {
    const entry = new LogEntry(
      sourceContext,
      message,
      LogLevel.Info,
      optionalParams
    );
    if (!this.shouldLog(entry.level)) return () => {};
    this.logToAppInsights(entry);
    return console.info.bind(
      console,
      ...[
        entry.buildConsoleString(),
        'color:green',
        'color:violet',
        ...entry.extraInfo.map((ei) => JSON.stringify(ei)),
      ]
    );
  };

  initAppInsights() {
    this.http
      .get<{ connectionString: string }>(
        `${this.environment.apiPath}/appInsights`
      )
      .subscribe((vm) => {
        if (!!vm.connectionString) {
          var angularPlugin = new AngularPlugin();
          const appInsights = new ApplicationInsights({
            config: {
              connectionString: vm.connectionString,
              maxAjaxCallsPerView: -1,
              extensions: [angularPlugin],
              extensionConfig: {
                [angularPlugin.identifier]: {
                  router: this.router,
                  errorServices: [new ErrorHandler()],
                },
              },
              cookieCfg: {
                enabled: false,
              },
            },
          });
          appInsights.addTelemetryInitializer(
            (env) =>
              this.verboseLogging ||
              env.baseData?.success == false ||
              (env.baseData?.severityLevel || 0) >= LogLevel.Warn - 1
          );
          appInsights.loadAppInsights();
          this.appInsights$.next(appInsights);
        }
      });
  }

  setUserId(userId?: number) {
    this.appInsights$
      .pipe(
        filter((ai) => !!ai),
        take(1)
      )
      .subscribe((appInsights) => {
        if (userId) {
          appInsights!.setAuthenticatedUserContext(userId.toString());
        } else {
          appInsights!.clearAuthenticatedUserContext();
        }
      });
  }

  warn = (sourceContext: string, message: string, ...optionalParams: any[]) => {
    const entry = new LogEntry(
      sourceContext,
      message,
      LogLevel.Warn,
      optionalParams
    );
    if (!this.shouldLog(entry.level)) return () => {};
    this.logToAppInsights(entry);
    return console.warn.bind(
      console,
      ...[
        entry.buildConsoleString(),
        'color:orange',
        'color:violet',
        ...entry.extraInfo.map((ei) => JSON.stringify(ei)),
      ]
    );
  };
}

class LogEntry {
  // Public Properties
  private entryDate: Date = new Date();

  constructor(
    public sourceContext: string,
    public message: string,
    public level: LogLevel,
    public extraInfo: any[] = []
  ) {}

  private formatParams(params: any[]): string {
    return JSON.stringify(params);
  }

  buildAppInsightString(): string {
    let msg = `${this.message}`; //${this.formatParams(this.extraInfo)}`;
    return msg;
  }

  buildConsoleString(): string {
    var ts = this.entryDate.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h24',
      fractionalSecondDigits: 3,
    } as any);
    let msg = `%c[${ts} ${LogLevel[this.level].padEnd(5)}] %c<${
      this.sourceContext
    }> ${this.message}`;
    return msg;
  }
}
